import { SquaresFour, Users, FileText, Gear, DotsThreeVertical, SignOut, SidebarSimple, Sidebar as SidebarIcon, BookOpen, IdentificationCard, CheckSquare, Ticket, EnvelopeSimple, Envelope, ShieldCheck, X, ArrowUUpLeft, Target, PhoneList } from '@phosphor-icons/react';
import { useMobileMenu } from '@/lib/mobile-menu-context';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession, signOut } from '@/lib/auth-client';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { API_URL } from '@/lib/api';
import { usePermissions } from '@/hooks/usePermissions';

type MenuItem = {
  icon: any;
  label: string;
  path: string;
  badgeKey?: 'resiliations' | 'lcbft';
  /** Permissions ; il suffit d'en avoir une pour voir l'item (admins voient tout). */
  anyOf?: string[];
  /** Rubrique du menu (Paramètres reste à part, en bas). */
  section?: 'Pilotage' | 'Clients' | 'Production' | 'Suivi' | 'Ressources';
};

// Menu regroupé par usage, dans l'ordre d'affichage des rubriques.
const menuItems: MenuItem[] = [
  { icon: SquaresFour, label: 'Tableau de bord', path: '/dashboard', section: 'Pilotage' },
  { icon: Users, label: 'Contacts', path: '/contacts', anyOf: ['contacts.view'], section: 'Clients' },
  { icon: Target, label: 'Opportunités', path: '/opportunites', anyOf: ['contacts.view_all', 'contacts.view_team', 'contacts.view_own'], section: 'Clients' },
  { icon: PhoneList, label: 'Power dialer', path: '/power-dialer', anyOf: ['dialer.view', 'dialer.manage'], section: 'Clients' },
  { icon: Envelope, label: 'Mail', path: '/mail', anyOf: ['mail.view', 'mail.send', 'mail.config'], section: 'Clients' },
  { icon: FileText, label: 'Devis', path: '/devis', anyOf: ['devis.view'], section: 'Production' },
  { icon: FileText, label: 'Contrats', path: '/contrats', anyOf: ['contrats.view'], section: 'Production' },
  { icon: EnvelopeSimple, label: 'Résiliation', path: '/resiliations', badgeKey: 'resiliations', anyOf: ['resiliations.view_all', 'resiliations.view_own', 'resiliations.view_team'], section: 'Production' },
  { icon: CheckSquare, label: 'Tâches', path: '/taches', anyOf: ['taches.view', 'taches.view_own'], section: 'Suivi' },
  { icon: Ticket, label: 'Tickets', path: '/tickets', anyOf: ['tickets.view', 'tickets.view_own'], section: 'Suivi' },
  { icon: ShieldCheck, label: 'Conformité', path: '/conformite', badgeKey: 'lcbft', anyOf: ['compliance.view_lcbft'], section: 'Suivi' },
  { icon: BookOpen, label: 'Éditique', path: '/editique', anyOf: ['editique.view'], section: 'Ressources' },
  { icon: IdentificationCard, label: 'Annuaire', path: '/annuaire', anyOf: ['annuaire.view'], section: 'Ressources' },
  { icon: ArrowUUpLeft, label: 'Bordereaux / Commissions', path: '/commissions/reprises', anyOf: ['commissions.reprises_view', 'commissions.reprises_manage', 'commissions.bordereaux_import'], section: 'Ressources' },
  { icon: Gear, label: 'Paramètres', path: '/parametres' },
];

/** Ruban des couleurs d'univers produits, comme en haut des pages du site. */
const RUBAN = ['bg-sante-vivid', 'bg-emprunteur-vivid', 'bg-auto-vivid', 'bg-moto-vivid', 'bg-habitation-vivid', 'bg-animal-vivid', 'bg-prevoyance-vivid'];

function useResiliationsPendingCount() {
  return useQuery({
    queryKey: ['resiliations', 'pending-count'],
    queryFn: async () => {
      try {
        const r = await fetch(`${API_URL}/resiliations/pending-count`, {
          credentials: 'include',
        });
        if (!r.ok) return 0;
        const j = await r.json();
        return typeof j.count === 'number' ? j.count : 0;
      } catch {
        return 0;
      }
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

function useLcbftAlertsCount(enabled: boolean) {
  return useQuery({
    queryKey: ['lcbft-alerts-count'],
    queryFn: async () => {
      try {
        const r = await fetch(`${API_URL}/lcbft/alerts`, { credentials: 'include' });
        if (!r.ok) return 0;
        const j = await r.json();
        return Array.isArray(j) ? j.length : 0;
      } catch {
        return 0;
      }
    },
    enabled,
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { data: session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: resiliationsCount } = useResiliationsPendingCount();
  const { open: mobileOpen, close: closeMobile } = useMobileMenu();
  const { hasAnyPermission } = usePermissions();
  const { data: lcbftCount } = useLcbftAlertsCount(hasAnyPermission(['compliance.view_lcbft']));

  // Filtre les items selon les permissions (les items sans `anyOf` sont toujours visibles).
  const visibleMenuItems = menuItems.filter(
    (item) => !item.anyOf || hasAnyPermission(item.anyOf),
  );

  // Sur mobile : un clic sur un item ferme le drawer.
  const navigateAndCloseMobile = (path: string) => {
    navigate(path);
    closeMobile();
  };

  const badgeFor = (key?: MenuItem['badgeKey']) => {
    if (key === 'resiliations' && resiliationsCount && resiliationsCount > 0) {
      return resiliationsCount;
    }
    if (key === 'lcbft' && lcbftCount && lcbftCount > 0) {
      return lcbftCount;
    }
    return null;
  };

  // Sur mobile : largeur fixe (260px), forcer l'état déplié quand le drawer est ouvert.
  const isMobileOpen = mobileOpen;
  const widthClass = isMobileOpen ? 'w-[260px]' : collapsed ? 'w-[68px]' : 'w-[256px]';

  return (
    <TooltipProvider delayDuration={0}>
      {/* Backdrop mobile (clic = ferme le drawer) — fade in/out */}
      <div
        onClick={closeMobile}
        className={`fixed inset-0 z-40 bg-ink/40 md:hidden transition-opacity duration-300 ${
          isMobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
      />
      {/* Mobile drawer : slide depuis la gauche. Desktop : sticky classique avec animation collapse/expand. */}
      <div
        className={`${widthClass} h-screen flex flex-col transition-[transform,width] duration-300 ease-in-out fixed inset-y-0 left-0 z-50 md:sticky md:top-0 md:translate-x-0 bg-ink text-[#C9D4E2] ${
          isMobileOpen ? 'translate-x-0 shadow-xl md:shadow-none' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <div className="flex h-1.5 shrink-0" aria-hidden="true">
          {RUBAN.map((c) => <div key={c} className={`flex-1 ${c}`} />)}
        </div>
        {/* Header — logo + toggle (desktop) ou bouton fermer (mobile) */}
        <div className={`h-16 flex items-center px-3 gap-2 ${collapsed && !isMobileOpen ? 'justify-center' : 'justify-between'}`}>
          {(!collapsed || isMobileOpen) && (
            <img src="/images/logo-finance-blanc.png" alt="Finance For You" className="h-6 w-auto ml-2" />
          )}
          {isMobileOpen ? (
            <button
              onClick={closeMobile}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors cursor-pointer md:hidden"
              aria-label="Fermer le menu"
            >
              <X className="size-4 text-[#C9D4E2]" />
            </button>
          ) : (
            <button
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? 'Déplier le menu' : 'Replier le menu'}
              className="w-8 h-8 hidden md:flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
            >
              {collapsed ? (
                <SidebarIcon className="size-4 text-[#C9D4E2]" />
              ) : (
                <SidebarSimple className="size-4 text-[#C9D4E2]" />
              )}
            </button>
          )}
        </div>

        {/* Menu */}
        <div className="flex-1 overflow-y-auto py-2">
          <nav className="px-2.5 space-y-0.5" aria-label="Navigation principale">
            {visibleMenuItems.map((item, index) => {
              const isActive = location.pathname === item.path ||
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path + '/'));
              const badge = badgeFor(item.badgeKey);

              // En mode mobile drawer ouvert, on n'affiche pas la version collapsed.
              const showCollapsed = collapsed && !isMobileOpen;

              // Titre de rubrique au premier item de chaque rubrique (Paramètres : séparé par un filet).
              const precedente = visibleMenuItems[index - 1]?.section;
              const titre = item.section !== precedente ? item.section : undefined;
              const entete = titre ? (
                showCollapsed ? (
                  index > 0 && <div key={`s-${titre}`} className="mx-2 my-2 border-t border-white/10" />
                ) : (
                  <p key={`s-${titre}`} className={`px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8FA3BC] ${index > 0 ? 'pt-4' : 'pt-1'}`}>
                    {titre}
                  </p>
                )
              ) : !item.section && precedente ? (
                <div key="s-fin" className="mx-2 my-3 border-t border-white/10" />
              ) : null;

              const button = (
                <button
                  key={item.path}
                  onClick={() => navigateAndCloseMobile(item.path)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-goutte-sm text-sm transition-colors cursor-pointer overflow-hidden ${
                    isActive
                      ? 'bg-brand/20 text-white font-semibold'
                      : 'text-[#C9D4E2] font-medium hover:bg-white/8 hover:text-white'
                  } ${showCollapsed ? 'justify-center' : ''}`}
                >
                  <item.icon className="size-4 flex-shrink-0" />
                  <span
                    className={`text-left whitespace-nowrap truncate min-w-0 transition-all duration-200 ${
                      showCollapsed ? 'opacity-0 w-0 -ml-3' : 'opacity-100 flex-1'
                    }`}
                    title={item.label}
                  >
                    {item.label}
                  </span>
                  {badge !== null && !showCollapsed && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold bg-auto-vivid text-ink flex-shrink-0">
                      {badge}
                    </span>
                  )}
                  {badge !== null && showCollapsed && (
                    <span className="absolute ml-6 -mt-4 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold bg-auto-vivid text-ink">
                      {badge}
                    </span>
                  )}
                </button>
              );

              if (showCollapsed) {
                return [
                  entete,
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent side="right">
                      {item.label}
                      {badge !== null && ` (${badge})`}
                    </TooltipContent>
                  </Tooltip>,
                ];
              }

              return [entete, button];
            })}
          </nav>
        </div>

        {/* User Profile */}
        <div className="px-2.5 pb-4 pt-3 border-t border-white/10">
          {session?.user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-3 p-2 rounded-goutte-sm hover:bg-white/8 transition-colors w-full cursor-pointer overflow-hidden ${collapsed && !isMobileOpen ? 'justify-center' : ''}`}>
                  <div className="size-9 rounded-full bg-brand flex items-center justify-center text-sm font-bold text-ink flex-shrink-0">
                    {session.user.name?.[0]?.toUpperCase() || session.user.email?.[0]?.toUpperCase()}
                  </div>
                  <div
                    className={`flex items-center gap-3 min-w-0 transition-all duration-200 ${
                      collapsed && !isMobileOpen ? 'opacity-0 w-0 -ml-3' : 'opacity-100 flex-1'
                    }`}
                  >
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-semibold text-white truncate">
                          {session.user.name || 'Utilisateur'}
                        </p>
                        <p className="text-xs text-[#8FA3BC] truncate">
                          {session.user.email}
                        </p>
                      </div>
                      <DotsThreeVertical className="size-4 text-[#8FA3BC] flex-shrink-0" />
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" side="right" sideOffset={8}>
                <div className="flex items-center gap-3 p-3 border-b">
                  <div className="size-9 rounded-full bg-[#032055]/10 flex items-center justify-center text-sm font-medium text-ink">
                    {session.user.name?.[0]?.toUpperCase() || session.user.email?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {session.user.name || 'Utilisateur'}
                    </p>
                    <p className="text-xs text-ink-muted truncate">
                      {session.user.email}
                    </p>
                  </div>
                </div>

                <DropdownMenuItem
                  className="cursor-pointer text-red-600 focus:text-red-600"
                  onClick={async () => {
                    await signOut();
                    window.location.href = '/login';
                  }}
                >
                  <SignOut className="mr-2 size-4" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
